import type {
	ConstantsAuditLogStruct,
	ConstantsPublicTeamInviteResponse,
	ConstantsPublicTeamMemberResponse,
	ConstantsTeamResponse,
	ConstantsTeamUserInviteStruct,
} from "@gosaas/types";
import { hasPermission, type Permission, type Role } from "@gosaas/types/rbac";
import {
	IconCalendar,
	IconDeviceDesktopAnalytics,
	IconDeviceMobile,
	IconGlobe,
} from "@tabler/icons-react";
import type { ColumnDef, RowData } from "@tanstack/react-table";
import type React from "react";
import { useState } from "react";
import { UAParser } from "ua-parser-js";
import { AcceptTeamDialog } from "@/components/dialogs/accept-team";
import { CancelInviteDialog } from "@/components/dialogs/cancel-invite";
import { DeclineTeamDialog } from "@/components/dialogs/decline-team";
import { LeaveTeamDialog } from "@/components/dialogs/leave-team";
import { MemberNoteDialog } from "@/components/dialogs/member-note";
import { RemoveMemberDialog } from "@/components/dialogs/remove-member";
import { Loader } from "@/components/Loader";
import { useTeam } from "@/components/providers/team";
import { ChangeRole } from "@/components/selects/change-role";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { CopyButton } from "../ui/copy-button";
import { Skeleton } from "../ui/skeleton";
export type ExtendedColumnDef<
	TData extends RowData,
	TValue = unknown,
> = ColumnDef<TData, TValue> & {
	label?: string;
	isVisible?: boolean;
	enableSorting?: boolean;
	enableHiding?: boolean;
};

export const auditLogColumns: ExtendedColumnDef<ConstantsAuditLogStruct>[] = [
	{
		header: "Action",
		accessorKey: "action",
		cell: ({ row }) => {
			const action = row.getValue("action") as string;
			const getActionVariant = (action: string) => {
				if (action.includes("SUCCESS")) return "success";
				if (action.includes("FAILED")) return "destructive";
				if (action.includes("INVITE")) return "outline";
				return "default";
			};

			return (
				<Badge variant={getActionVariant(action)} className="font-mono text-xs">
					{action.replace(/_/g, " ")}
				</Badge>
			);
		},
		enableSorting: false,
		enableHiding: true,
	},
	{
		header: "IP Address",
		accessorKey: "ip_address",
		cell: ({ row }) => {
			const ip = row.getValue("ip_address") as string;
			return (
				<code className="px-2 py-1 bg-muted rounded text-xs font-mono">
					{ip}
				</code>
			);
		},
		enableSorting: false,
		enableHiding: true,
	},
	{
		header: "User Agent",
		accessorKey: "user_agent",
		cell: ({ row }) => {
			const userAgent = row.getValue("user_agent") as string;
			const [userAgentInfo] = useState(() => {
				return UAParser(userAgent);
			});

			if (!userAgentInfo) return <Skeleton className="h-4 w-32" />;

			const deviceIcon =
				userAgentInfo.device?.type === "mobile"
					? IconDeviceMobile
					: IconDeviceDesktopAnalytics;
			const DeviceIcon = deviceIcon;

			return (
				<HoverCard>
					<HoverCardTrigger
						{...({ asChild: true } as unknown as React.ComponentProps<
							typeof HoverCardTrigger
						>)}
					>
						<Button
							className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors text-left max-w-[280px]"
							variant="ghost"
						>
							<DeviceIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
							<div className="min-w-0 flex-1">
								<div className="flex items-center gap-2 text-sm">
									<span className="font-medium truncate">
										{userAgentInfo?.browser.name}
									</span>
									<span className="text-muted-foreground text-xs">
										{userAgentInfo?.browser.version?.split(".")[0]}
									</span>
								</div>
								<div className="text-xs text-muted-foreground truncate">
									{userAgentInfo?.os.name} {userAgentInfo?.os.version}
								</div>
							</div>
						</Button>
					</HoverCardTrigger>
					<HoverCardContent className="w-80 bg-background">
						<div className="space-y-3">
							<div className="flex items-center gap-2">
								<IconGlobe className="h-4 w-4 text-muted-foreground" />
								<span className="font-medium">Browser Details</span>
							</div>
							<div className="grid grid-cols-2 gap-3 text-sm">
								<div>
									<div className="text-muted-foreground">Browser</div>
									<div className="font-mono">
										{userAgentInfo?.browser.name}{" "}
										{userAgentInfo?.browser.version}
									</div>
								</div>
								<div>
									<div className="text-muted-foreground">Engine</div>
									<div className="font-mono">
										{userAgentInfo?.engine.name} {userAgentInfo?.engine.version}
									</div>
								</div>
								<div>
									<div className="text-muted-foreground">OS</div>
									<div className="font-mono">
										{userAgentInfo?.os.name} {userAgentInfo?.os.version}
									</div>
								</div>
								<div>
									<div className="text-muted-foreground">Device</div>
									<div className="font-mono">
										{userAgentInfo?.device?.type || "Desktop"}
									</div>
								</div>
							</div>
							<div className="pt-2 border-t">
								<div className="flex items-center justify-between">
									<div className="text-muted-foreground text-xs">
										Raw User Agent
									</div>
								</div>
								<div className="mt-1 p-2 bg-muted rounded text-xs font-mono break-all relative">
									<CopyButton
										value={userAgent}
										className="absolute top-1 right-1"
									/>
									<div className="pr-8 break-all">{userAgent}</div>
								</div>
							</div>
						</div>
					</HoverCardContent>
				</HoverCard>
			);
		},
		enableSorting: false,
		enableHiding: true,
	},
	{
		header: "Metadata",
		accessorKey: "metadata",
		cell: ({ row }) => {
			try {
				const metadata = JSON.parse(row.getValue("metadata") as string);
				return (
					<div className="space-y-1">
						{Object.entries(metadata).map(([key, value]) => (
							<div key={key} className="flex items-center gap-2 text-xs">
								<span className="text-muted-foreground font-medium min-w-0">
									{key}:
								</span>
								<span className="font-mono bg-muted px-1 rounded">
									{String(value)}
								</span>
							</div>
						))}
					</div>
				);
			} catch {
				return null;
			}
		},
		enableSorting: false,
		enableHiding: true,
	},
	{
		accessorKey: "created_at",
		header: "Created At",
		cell: ({ row }) => {
			const rawDateTime = row.getValue("created_at") as string;
			const date = new Date(rawDateTime);
			return (
				<HoverCard>
					<HoverCardTrigger
						{...({ asChild: true } as unknown as React.ComponentProps<
							typeof HoverCardTrigger
						>)}
					>
						<div className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-1 rounded">
							<IconCalendar className="h-4 w-4 text-muted-foreground" />
							<div>
								<div className="text-sm font-medium">
									{date.toLocaleDateString()}
								</div>
								<div className="text-xs text-muted-foreground">
									{date.toLocaleTimeString()}
								</div>
							</div>
						</div>
					</HoverCardTrigger>
					<HoverCardContent className="w-80 bg-background">
						<div className="space-y-2">
							<h4 className="text-sm font-semibold">Date & Time Details</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="text-muted-foreground">Relative Time:</span>
									<div className="font-mono text-xs mt-1">
										{(() => {
											const now = new Date();
											const diffMs = now.getTime() - date.getTime();
											const diffMins = Math.floor(diffMs / (1000 * 60));
											const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
											const diffDays = Math.floor(
												diffMs / (1000 * 60 * 60 * 24),
											);

											if (diffMins < 60) {
												return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
											}
											if (diffHours < 24) {
												return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
											}
											return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
										})()}
									</div>
								</div>
								<div>
									<span className="text-muted-foreground">UTC Time:</span>
									<div className="font-mono text-xs mt-1">
										{date.toUTCString()}
									</div>
								</div>
								<div>
									<span className="text-muted-foreground">ISO Format:</span>
									<div className="font-mono text-xs mt-1">
										{date.toISOString()}
									</div>
								</div>
								<div className="mt-1 p-2 bg-muted rounded text-xs font-mono break-all relative">
									<CopyButton
										value={rawDateTime}
										className="absolute top-1 right-1"
									/>
									<div className="pr-8 break-all">{rawDateTime}</div>
								</div>
							</div>
						</div>
					</HoverCardContent>
				</HoverCard>
			);
		},
		enableSorting: true,
		enableHiding: false,
	},
];

export const teamMemberColumns: ExtendedColumnDef<ConstantsPublicTeamMemberResponse>[] =
	[
		{
			header: "Emoji",
			accessorKey: "emoji",
			cell: ({ row }) => (
				<div className="flex items-center gap-2">
					<span className="text-2xl">
						{decodeURIComponent(row.getValue("emoji"))}
					</span>
				</div>
			),
		},
		{
			header: "Name",
			accessorKey: "first_name",
		},
		{
			header: "Last Name",
			accessorKey: "last_name",
		},
		{
			header: "Email",
			accessorKey: "email",
		},
		{
			header: "Role",
			accessorKey: "role",
			cell: ({ row }) => {
				const { teamID, teams, loading: isLoadingTeam } = useTeam();
				if (isLoadingTeam) return <Loader />;
				const team = teams.find((t) => t.id === teamID);
				if (
					team?.type !== "personal" &&
					hasPermission(team?.role as Role, "members:update")
				) {
					return (
						<div className="flex items-center gap-2">
							<ChangeRole memberId={row.original.id} />
						</div>
					);
				}
				return (
					<div className="flex items-center gap-2">
						{String(row.getValue("role")).charAt(0).toUpperCase() +
							String(row.getValue("role")).slice(1)}
					</div>
				);
			},
		},
		{
			header: "Created At",
			accessorKey: "created_at",
			cell: ({ row }) => {
				const date = new Date(row.getValue("created_at"));
				return (
					<div title={row.getValue("created_at")} className="">
						{date.toLocaleString()}
					</div>
				);
			},
			enableSorting: true,
		},
		{
			header: "Actions",
			accessorKey: "actions",
			cell: ({ row }) => {
				const { teamID, teams, loading: isLoadingTeam } = useTeam();
				if (isLoadingTeam) return <Loader />;
				const team = teams.find((t) => t.id === teamID);
				const canUpdateMember = hasPermission(
					team?.role as Role,
					"members:update" as Permission,
				);
				const canDeleteMember = hasPermission(
					team?.role as Role,
					"members:delete" as Permission,
				);
				return (
					<div className="flex items-center gap-2">
						<MemberNoteDialog
							row={row.original.notes as string}
							memberID={row.original.id as string}
							disabled={!canUpdateMember}
						/>
						<RemoveMemberDialog
							memberID={row.original.id as string}
							disabled={!canDeleteMember}
						/>
					</div>
				);
			},
		},
	];

export const teamInviteColumns: ExtendedColumnDef<ConstantsPublicTeamInviteResponse>[] =
	[
		{
			header: "Email",
			accessorKey: "email",
		},
		{
			header: "Role",
			accessorKey: "role",
			cell: ({ row }) => {
				return (
					<div className="flex items-center gap-2">
						{String(row.getValue("role")).charAt(0).toUpperCase() +
							String(row.getValue("role")).slice(1)}
					</div>
				);
			},
		},
		{
			header: "Expires At",
			accessorKey: "expires_at",
			cell: ({ row }) => {
				const date = new Date(row.getValue("expires_at"));
				return (
					<div title={row.getValue("expires_at")}>{date.toLocaleString()}</div>
				);
			},
		},
		{
			header: "Actions",
			accessorKey: "actions",
			cell: ({ row }) => {
				return (
					<div className="flex items-center gap-2">
						<CancelInviteDialog inviteID={row.original.id} />
					</div>
				);
			},
		},
	];

export const userTeamColumns: ExtendedColumnDef<ConstantsTeamResponse>[] = [
	{
		header: "Emoji",
		accessorKey: "emoji",
		cell: ({ row }) => (
			<div className="flex items-center gap-2">
				<span className="text-2xl">
					{decodeURIComponent(row.getValue("emoji"))}
				</span>
			</div>
		),
	},
	{
		header: "Name",
		accessorKey: "name",
	},
	{
		header: "Role",
		accessorKey: "role",
		cell: ({ row }) => {
			return (
				<div className="flex items-center gap-2">
					{String(row.getValue("role")).charAt(0).toUpperCase() +
						String(row.getValue("role")).slice(1)}
				</div>
			);
		},
	},
	{
		header: "Type",
		accessorKey: "type",
		cell: ({ row }) => {
			return (
				<div className="flex items-center gap-2">
					{String(row.getValue("type")).charAt(0).toUpperCase() +
						String(row.getValue("type")).slice(1)}
				</div>
			);
		},
	},
	{
		header: "Created At",
		accessorKey: "created_at",
		cell: ({ row }) => {
			const date = new Date(row.getValue("created_at"));
			return (
				<div title={row.getValue("created_at")}>{date.toLocaleString()}</div>
			);
		},
	},
	{
		header: "Joined At",
		accessorKey: "joined_at",
		cell: ({ row }) => {
			const date = new Date(row.getValue("joined_at"));
			return (
				<div title={row.getValue("joined_at")}>{date.toLocaleString()}</div>
			);
		},
	},
	{
		header: "Actions",
		accessorKey: "actions",
		cell: ({ row }) => {
			return <LeaveTeamDialog teamID={row.original.id} />;
		},
	},
];

export const userInviteColumns: ExtendedColumnDef<ConstantsTeamUserInviteStruct>[] =
	[
		{
			header: "Emoji",
			accessorKey: "emoji",
			cell: ({ row }) => (
				<div className="flex items-center gap-2">
					<span className="text-2xl">
						{decodeURIComponent(row.getValue("emoji"))}
					</span>
				</div>
			),
		},
		{
			header: "Name",
			accessorKey: "team_name",
		},
		{
			header: "Role",
			accessorKey: "role",
			cell: ({ row }) => {
				return (
					<div className="flex items-center gap-2">
						{String(row.getValue("role")).charAt(0).toUpperCase() +
							String(row.getValue("role")).slice(1)}
					</div>
				);
			},
		},
		{
			header: "Expires At",
			accessorKey: "expires_at",
			cell: ({ row }) => {
				const date = new Date(row.getValue("expires_at"));
				const now = new Date();
				const diffMs = date.getTime() - now.getTime();
				const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
				const diffMinutes = Math.floor(
					(diffMs % (1000 * 60 * 60)) / (1000 * 60),
				);
				const timeLeft = diffHours > 0 ? `${diffHours}h` : `${diffMinutes}m`;
				return (
					<div title={row.getValue("expires_at")}>
						{date.toLocaleString()} - ({timeLeft})
					</div>
				);
			},
		},
		{
			header: "Actions",
			accessorKey: "actions",
			cell: ({ row }) => {
				return (
					<div className="flex items-center gap-2">
						<AcceptTeamDialog inviteId={row.original.id} />
						<DeclineTeamDialog inviteId={row.original.id} />
					</div>
				);
			},
		},
	];
